import { RootState } from 'types/state';
import { ff } from 'helpers/featureFlags';
import { hasAttributeErrors } from '@redux/modules/form/selectors';
import { SubsectionContextType } from 'components/Section/shared/types';
import { withContext } from 'components/withContext/WithContext';
import { connect } from 'react-redux';
import { SubsectionContext } from 'components/Section/shared/SubsectionConnector';

/**
 * This will seem like it is introducing new errors to existing components,
 * however, those components most likely have 0 props defined, which would be
 * the root cause of the error.
 */
export interface StateFormErrorProps {
  attribute?: string
  attributeList?: Array<string>
  subsection: SubsectionContextType
}

export const mapStateFormErrorsToProps = <P extends StateFormErrorProps>(
  state: RootState,
  ownProps: P
) => {
  const props = {
    features: state.featureFlags,
  };

  if (ff(state.featureFlags).getActive('EN-4733')) {
    /*
    attributeList is a prop passed from higher level components to Generic on an as needed basis.
    It overrides attribute property and facilitates having error formatting shown if any of the array
    elements can be linked to an error through generic isValid method.
     */
    if (ownProps.attributeList !== undefined && ownProps.subsection.subsectionKey) {
      let attributeCombinedIsValid = true;

      ownProps.attributeList.forEach((attribute) => {
        if (ownProps.subsection.subsectionKey) {
          // this will always be true here, but needed to remove typescipt error
          const attributeIsValid = !hasAttributeErrors(
            state,
            ownProps.subsection.subsectionKey,
            attribute,
            ownProps.subsection.showRequiredErrors,
            ownProps.subsection.displayErrorsReviewOnly
          );
          attributeCombinedIsValid = attributeCombinedIsValid && attributeIsValid;
        }
      });
      const attributeCombinedHasErrors = !attributeCombinedIsValid;
      return {
        ...props,
        hasStateFormError: attributeCombinedHasErrors,
      };
    }

    if (ownProps.attribute !== undefined && ownProps.subsection.subsectionKey) {
      const attributeIsValid = !hasAttributeErrors(
        state,
        ownProps.subsection.subsectionKey,
        ownProps.attribute,
        ownProps.subsection.showRequiredErrors,
        ownProps.subsection.displayErrorsReviewOnly
      );
      return {
        ...props,
        hasStateFormError: !attributeIsValid,
      };
    }

    return {
      ...props,
      hasStateFormError: false,
    };
  }

  return props;
};

export interface StateFormErrorConnectorProps {
  hasStateFormError?: boolean
  features?: RootState['featureFlags']
  attribute?: string
}

export const stateFormErrorConnector = (Component: React.ComponentType) => {
  if (ff().fetch().getActive('EN-4733')) {
    return withContext(
      connect(mapStateFormErrorsToProps, undefined, undefined, {
        forwardRef: true,
      })(Component),
      SubsectionContext,
      'subsection'
    );
  }
  return Component;
};
